import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, map, tap } from 'rxjs'

import {
  InitialMessageRequest,
  InitialMessageResponse,
  Message,
  SendFeedbackPayload,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

export type GenerateDatasourcePayload = {
  question: string
  action: 'generate_from_prompt' | 'generate_from_openapi' | 'edit_datasource' | 'generate_database'
  openapi_url?: string
  current_system?: any
  session_id?: string
}

export type BuildWorkflowPayload = {
  query: string
  model?: string | null
  session_id?: string
  thread_id?: string
  decision?: string | null
  tool_name?: string | null
  feedback?: any | null
  resource_id?: string | null
  resource_definition_id?: string | null
  context?: any | null
}

@Injectable({ providedIn: 'root' })
export class AiIntegratorApi extends ApiHttpService {
  #integrator = `ai-engine/agents/integrator`

  constructor() {
    super(inject<string>('BASE_URL' as any), inject(HttpClient))
  }

  initialMessage(payload: InitialMessageRequest): Observable<InitialMessageResponse> {
    return this.post(`${this.#integrator}/initial`, payload)
  }

  getSSEActions(payload: InitialMessageRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#integrator}/actions`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEChat(payload: any): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#integrator}/chat`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateDatasource(
    payload: GenerateDatasourcePayload
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#integrator}/chat`, payload).pipe(
      map((response: any) => response || null),
      map((answerStr) => ({ message: answerStr }))
    )
  }

  getBuildWorkflowResponse(
    payload: BuildWorkflowPayload
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#integrator}/build-workflow`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)
          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      tap((raw: any) => console.log('[WB-RAW]', JSON.stringify(raw))),
      map((response: any) => this.#transformBuildWorkflowResponse(response)),
      tap((transformed: any) => console.log('[WB-TRANSFORMED]', JSON.stringify(transformed)))
    )
  }

  getBuildWorkflowInitial(): Observable<any> {
    return this.post(`${this.#integrator}/build-workflow/initial`, {})
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#integrator}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }

  #transformBuildWorkflowResponse(response: any): any {
    // Already in the handler's expected format (has type at root level)
    if (response?.type && !response?.answer) {
      return response
    }

    // Root-level interrupt: {"interrupt": {"operation": "...", "tool_name": "...", "data": {...}}}
    if (response?.interrupt) {
      const rawArgs = response.interrupt.data ?? {}
      const questions = (rawArgs.questions ?? []).map((q: any) => ({
        ...q,
        text: q.text ?? q.question,
        options: (q.options ?? []).map((opt: any, i: number) =>
          typeof opt === 'string' ? { id: `${q.id}_opt_${i}`, label: opt } : opt
        ),
      }))
      return {
        type: 'interrupt',
        content: {
          ...response.interrupt,
          args: { ...rawArgs, questions },
        },
      }
    }

    // Root-level end: {"end": {"operation": "workflow_saved"|"workflow_configured"|..., "answer": {...}}}
    if (response?.end) {
      const endOp = response.end.operation
      if (endOp === 'workflow_saved') {
        return {
          type: 'workflow_saved',
          content: response.end.answer ?? response.end,
        }
      }
      if (endOp === 'workflow_configured') {
        return {
          type: 'workflow_saved',
          content: response.end.answer ?? response.end,
        }
      }
      return {
        type: 'text',
        content: { text: JSON.stringify(response.end.answer ?? response.end) },
        streaming: false,
      }
    }

    // Root-level error: {"error": {"message": "...", "operation": "error"}}
    if (response?.error) {
      return {
        type: 'text',
        content: { text: response.error.message ?? 'An error occurred' },
        streaming: false,
        error: true,
      }
    }

    // Root-level debug: {"debug": {"step": "...", "status": "...", "info": "..."}}
    if (response?.debug) {
      return {
        type: 'summarization',
        content: { text: response.debug.info ?? '' },
      }
    }

    const answer = response?.answer
    if (!answer) {
      return response
    }

    const operation = answer.operation

    // Session message
    if (operation === 'session') {
      return {
        type: 'session',
        content: { thread_id: answer.thread_id },
      }
    }

    // Handle answer.message as a string (text content)
    if (typeof answer.message === 'string') {
      return {
        type: 'text',
        content: { text: answer.message },
        streaming: operation === 'streaming',
        id: response.id,
      }
    }

    // Handle answer.message as an array
    const messages = answer.message ?? []
    const firstMsg = messages[0]

    if (!firstMsg) {
      return response
    }

    const msgType = firstMsg.type

    if (msgType === 'function_call') {
      return {
        type: 'tool_call_chunk',
        chunk: {
          name: firstMsg.name ?? null,
          arguments: firstMsg.arguments,
          index: firstMsg.index,
        },
        streaming: operation === 'streaming',
      }
    }

    if (msgType === 'function_call_output') {
      return {
        type: 'tool_result',
        content: {
          tool_call_id: firstMsg.call_id,
          tool_name: firstMsg.name,
          result: firstMsg.output ?? firstMsg.content,
        },
      }
    }

    if (msgType === 'text') {
      return {
        type: 'text',
        content: { text: firstMsg.text ?? firstMsg.content ?? '' },
        streaming: operation === 'streaming',
        id: response.id ?? firstMsg.id,
      }
    }

    if (msgType === 'reasoning' || msgType === 'thinking') {
      const reasoningText = firstMsg.text ?? firstMsg.content ?? ''
      return {
        type: 'reasoning',
        content: { text: reasoningText },
        reasoning: reasoningText,
      }
    }

    return response
  }
}
